import { type ReactNode, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Library as LibraryIcon, Paperclip, Plus, Shuffle, X } from "lucide-react";
import { ApiError } from "@/lib/api";
import type { BroadcastMessage } from "@/lib/broadcasts";
import { type LibraryItem, expandSpintax, useCreateLibraryItem } from "@/lib/library";
import { ContentLibrary } from "@/components/inbox/ContentLibrary";
import { KIND_ICONS } from "@/components/inbox/libraryKinds";
import { pushToast } from "@/components/Toaster";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";

/** Telegram's limits: one message, and the text under a file. */
const MAX_TEXT = 4096;
const MAX_CAPTION = 1024;

const FIELD =
  "w-full rounded-lg border border-border bg-bg-primary px-3 py-2 text-sm leading-relaxed " +
  "placeholder:text-fg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";

function asMessage(item: LibraryItem): BroadcastMessage {
  return {
    id: item.id,
    kind: item.kind,
    title: item.title,
    text: item.text,
    file_name: item.file_name,
  };
}

export interface MessagesEditorProps {
  messages: BroadcastMessage[];
  onAdd: (message: BroadcastMessage) => void;
  onRemove: (id: string) => void;
  /** Extra control on a message's row — the card's "send to myself". */
  rowAction?: (message: BroadcastMessage) => ReactNode;
  disabled?: boolean;
}

/**
 * What a broadcast posts: its messages, in the order they take turns. A
 * message is a material of the content library — written here on the spot (and
 * kept in the library), or taken from what is already there.
 */
export function MessagesEditor({
  messages,
  onAdd,
  onRemove,
  rowAction,
  disabled = false,
}: MessagesEditorProps) {
  const { t } = useTranslation();
  const [libraryOpen, setLibraryOpen] = useState(false);

  const add = (item: LibraryItem) => {
    if (messages.some((m) => m.id === item.id)) {
      pushToast({ kind: "info", text: t("broadcasts.messages.already") });
      return;
    }
    onAdd(asMessage(item));
  };

  return (
    <div className="space-y-3">
      {messages.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-4 py-5 text-center text-sm text-fg-muted">
          {t("broadcasts.messages.empty")}
        </p>
      ) : (
        <ol className="space-y-2">
          {messages.map((message, index) => (
            <MessageRow
              key={message.id}
              index={index}
              message={message}
              onRemove={() => onRemove(message.id)}
              action={rowAction?.(message)}
              disabled={disabled}
            />
          ))}
        </ol>
      )}

      {messages.length > 1 && (
        <p className="text-xs text-fg-muted">{t("broadcasts.messages.rotation_hint")}</p>
      )}

      <Composer onCreated={add} disabled={disabled} onOpenLibrary={() => setLibraryOpen(true)} />

      <ContentLibrary
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        onPick={(item) => {
          add(item);
          setLibraryOpen(false);
        }}
      />
    </div>
  );
}
MessagesEditor.displayName = "MessagesEditor";

function MessageRow({
  index,
  message,
  onRemove,
  action,
  disabled,
}: {
  index: number;
  message: BroadcastMessage;
  onRemove: () => void;
  action?: ReactNode;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const Icon = KIND_ICONS[message.kind];
  const text = message.text ?? "";
  return (
    <li className="rounded-lg border border-border bg-bg-primary">
      <div className="flex items-center gap-2 px-3 py-2">
        <span className="shrink-0 font-mono text-xs font-semibold text-accent">#{index + 1}</span>
        <Icon size={15} strokeWidth={2} aria-hidden="true" className="shrink-0 text-fg-muted" />
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-2 text-left focus-visible:outline-none"
        >
          <span className="truncate text-sm font-medium text-fg-primary">{message.title}</span>
          <ChevronDown
            size={15}
            strokeWidth={2}
            aria-hidden="true"
            className={cn("shrink-0 text-fg-muted transition-transform", open && "rotate-180")}
          />
        </button>
        {action}
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          aria-label={t("broadcasts.messages.remove")}
          title={t("broadcasts.messages.remove")}
          className="shrink-0 rounded-md p-1.5 text-fg-muted transition-colors hover:bg-bg-elevated hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50"
        >
          <X size={15} strokeWidth={2} aria-hidden="true" />
        </button>
      </div>
      {open && (
        <div className="space-y-2 border-t border-border px-3 py-3">
          {message.file_name && (
            <p className="flex items-center gap-1.5 text-xs text-fg-muted">
              <Paperclip size={13} strokeWidth={2} aria-hidden="true" />
              {message.file_name}
            </p>
          )}
          {text ? (
            <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-fg-secondary">
              {text}
            </p>
          ) : (
            <p className="text-sm text-fg-muted">{t("broadcasts.messages.no_text")}</p>
          )}
        </div>
      )}
    </li>
  );
}

/** Write a new message here; it is kept in the library and added to the broadcast. */
function Composer({
  onCreated,
  onOpenLibrary,
  disabled,
}: {
  onCreated: (item: LibraryItem) => void;
  onOpenLibrary: () => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const create = useCreateLibraryItem();
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Which variant of the text the example shows; "another one" moves it on.
  const [seed, setSeed] = useState(1);
  const fileInput = useRef<HTMLInputElement>(null);

  const limit = file ? MAX_CAPTION : MAX_TEXT;
  const tooLong = text.length > limit;
  const hasVariants = /\{[^{}]*\|[^{}]*\}/.test(text);
  const example = useMemo(() => {
    // A cheap repeatable generator: the same seed shows the same variant.
    let state = seed * 9301 + 49297;
    return expandSpintax(text, () => {
      state = (state * 9301 + 49297) % 233280;
      return state / 233280;
    });
  }, [text, seed]);

  const submit = async () => {
    setError(null);
    try {
      const item = await create.mutateAsync({ text: text.trim(), file });
      onCreated(item);
      setText("");
      setFile(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("broadcasts.messages.create_error"));
    }
  };

  return (
    <div className="space-y-2 rounded-lg border border-border bg-bg-secondary p-3">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        disabled={disabled}
        placeholder={t("broadcasts.messages.placeholder") ?? ""}
        aria-label={t("broadcasts.messages.new")}
        className={cn(FIELD, "min-h-[6rem] resize-y")}
      />
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-fg-muted">
        <span>{t("broadcasts.messages.format_hint")}</span>
        <span className={cn("font-mono tabular-nums", tooLong && "text-danger")}>
          {text.length}/{limit}
        </span>
      </div>

      {hasVariants && (
        <div className="rounded-md bg-bg-elevated/60 px-3 py-2 text-xs">
          <div className="mb-1 flex items-center justify-between gap-2 text-fg-muted">
            <span>{t("broadcasts.messages.example")}</span>
            <button
              type="button"
              onClick={() => setSeed((s) => s + 1)}
              className="inline-flex items-center gap-1 text-accent hover:underline focus-visible:outline-none"
            >
              <Shuffle size={12} strokeWidth={2} aria-hidden="true" />
              {t("broadcasts.messages.another")}
            </button>
          </div>
          <p className="whitespace-pre-wrap break-words text-fg-secondary">{example}</p>
        </div>
      )}

      {file && (
        <p className="flex items-center gap-1.5 text-xs text-fg-secondary">
          <Paperclip size={13} strokeWidth={2} aria-hidden="true" className="text-fg-muted" />
          <span className="truncate">{file.name}</span>
          <button
            type="button"
            onClick={() => setFile(null)}
            aria-label={t("broadcasts.messages.detach")}
            className="rounded p-0.5 text-fg-muted hover:text-danger"
          >
            <X size={13} strokeWidth={2} aria-hidden="true" />
          </button>
        </p>
      )}
      {error && <p className="text-xs text-danger">{error}</p>}

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileInput}
          type="file"
          className="hidden"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            e.target.value = "";
          }}
        />
        <Button
          type="button"
          size="sm"
          disabled={disabled || tooLong || (!text.trim() && !file)}
          loading={create.isPending}
          onClick={submit}
          iconLeft={<Plus size={15} strokeWidth={2.5} aria-hidden="true" />}
        >
          {t("broadcasts.messages.add")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => fileInput.current?.click()}
          iconLeft={<Paperclip size={15} strokeWidth={2} aria-hidden="true" />}
        >
          {t("broadcasts.messages.attach")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={disabled}
          onClick={onOpenLibrary}
          iconLeft={<LibraryIcon size={15} strokeWidth={2} aria-hidden="true" />}
          className="ml-auto"
        >
          {t("broadcasts.messages.from_library")}
        </Button>
      </div>
    </div>
  );
}
